import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, Edit2, Check, X, Loader2, Sprout } from 'lucide-react';
import canvassingService from '../../services/canvassingService';
import ConfirmDialog from '../ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';

const CategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingPhrase, setEditingPhrase] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newPhraseCategoryId, setNewPhraseCategoryId] = useState(null);
  const [newPhraseText, setNewPhraseText] = useState('');
  const [error, setError] = useState(null);
  const { confirmDialog, confirm } = useConfirm();

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const data = await canvassingService.getCategories();
      setCategories(data.categories || []);
    } catch (err) {
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const toggleExpand = (id) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSeedDefaults = async () => {
    try {
      await canvassingService.seedCategories();
      await loadCategories();
    } catch (err) {
      setError('Failed to seed categories');
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await canvassingService.createCategory({ category_name: newCategoryName.trim() });
      setNewCategoryName('');
      setShowNewCategory(false);
      await loadCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async (id, data) => {
    try {
      await canvassingService.updateCategory(id, data);
      setEditingCategory(null);
      await loadCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update category');
    }
  };

  const handleDeleteCategory = async (id) => {
    const ok = await confirm({
      title: 'Delete Category',
      message: 'This will permanently delete this category and all its search phrases.',
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      await canvassingService.deleteCategory(id);
      await loadCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete category');
    }
  };

  const handleAddPhrase = async (categoryId) => {
    if (!newPhraseText.trim()) return;
    try {
      await canvassingService.addPhrase(categoryId, { search_phrase: newPhraseText.trim() });
      setNewPhraseText('');
      setNewPhraseCategoryId(null);
      await loadCategories();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add phrase');
    }
  };

  const handleDeletePhrase = async (categoryId, phraseId) => {
    try {
      await canvassingService.deletePhrase(categoryId, phraseId);
      await loadCategories();
    } catch (err) {
      setError('Failed to delete phrase');
    }
  };

  const handleToggleActive = async (categoryId, currentActive) => {
    await handleUpdateCategory(categoryId, { is_active: !currentActive });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-pest-blue" size={24} />
        <span className="ml-2 text-gray-500">Loading categories...</span>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center justify-between">
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={() => setShowNewCategory(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-pest-blue text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          <Plus size={16} /><span>Add Category</span>
        </button>
        <button
          onClick={handleSeedDefaults}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
        >
          <Sprout size={16} /><span>Seed Defaults</span>
        </button>
        <span className="text-sm text-gray-500">
          {categories.length} categories, {categories.reduce((sum, c) => sum + (c.phrases?.length || 0), 0)} phrases
        </span>
      </div>

      {/* New category input */}
      {showNewCategory && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center space-x-3">
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Category name..."
            className="flex-1 px-3 py-2 border rounded text-sm"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
          />
          <button onClick={handleCreateCategory} className="text-green-600 hover:text-green-800"><Check size={18} /></button>
          <button onClick={() => { setShowNewCategory(false); setNewCategoryName(''); }} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
      )}

      {/* Categories list */}
      <div className="space-y-1">
        {categories.map((cat) => {
          const isExpanded = expandedCategories.has(cat.id);
          const isEditing = editingCategory === cat.id;

          return (
            <div key={cat.id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Category header */}
              <div
                className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                  !cat.is_active ? 'opacity-50' : ''
                }`}
                onClick={() => toggleExpand(cat.id)}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {isExpanded ? <ChevronDown size={16} className="text-gray-400 shrink-0" /> : <ChevronRight size={16} className="text-gray-400 shrink-0" />}

                  {isEditing ? (
                    <input
                      type="text"
                      defaultValue={cat.category_name}
                      className="px-2 py-1 border rounded text-sm flex-1"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleUpdateCategory(cat.id, { category_name: e.target.value });
                        }
                        if (e.key === 'Escape') setEditingCategory(null);
                      }}
                      autoFocus
                    />
                  ) : (
                    <span className="font-medium text-gray-800 text-sm truncate">{cat.category_name}</span>
                  )}

                  {cat.serper_endpoint && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                      cat.serper_endpoint === 'maps' ? 'bg-purple-100 text-purple-700' :
                      cat.serper_endpoint === 'places' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {cat.serper_endpoint}
                    </span>
                  )}

                  <span className="text-xs text-gray-400 shrink-0">
                    {cat.phrases?.length || 0} phrases
                    {cat.lead_count > 0 && ` · ${cat.lead_count} leads`}
                  </span>
                </div>

                <div className="flex items-center space-x-2 ml-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => handleToggleActive(cat.id, cat.is_active)}
                    className={`text-xs px-2 py-1 rounded ${cat.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                  >
                    {cat.is_active ? 'Active' : 'Inactive'}
                  </button>
                  <button onClick={() => setEditingCategory(cat.id)} className="text-gray-400 hover:text-blue-600">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDeleteCategory(cat.id)} className="text-gray-400 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Phrases (expanded) */}
              {isExpanded && (
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-2">
                  {cat.phrases?.map((phrase) => (
                    <div key={phrase.id} className="flex items-center justify-between py-1.5 px-2 hover:bg-white rounded group">
                      {editingPhrase === phrase.id ? (
                        <input
                          type="text"
                          defaultValue={phrase.search_phrase}
                          className="px-2 py-1 border rounded text-sm flex-1 mr-2"
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                              await canvassingService.updatePhrase(cat.id, phrase.id, { search_phrase: e.target.value });
                              setEditingPhrase(null);
                              loadCategories();
                            }
                            if (e.key === 'Escape') setEditingPhrase(null);
                          }}
                          autoFocus
                        />
                      ) : (
                        <span className="text-sm text-gray-600">"{phrase.search_phrase}"</span>
                      )}
                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => setEditingPhrase(phrase.id)} className="text-gray-400 hover:text-blue-600 p-1">
                          <Edit2 size={12} />
                        </button>
                        <button onClick={() => handleDeletePhrase(cat.id, phrase.id)} className="text-gray-400 hover:text-red-600 p-1">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add phrase */}
                  {newPhraseCategoryId === cat.id ? (
                    <div className="flex items-center space-x-2 mt-2">
                      <input
                        type="text"
                        value={newPhraseText}
                        onChange={(e) => setNewPhraseText(e.target.value)}
                        placeholder="New search phrase..."
                        className="flex-1 px-2 py-1 border rounded text-sm"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleAddPhrase(cat.id)}
                      />
                      <button onClick={() => handleAddPhrase(cat.id)} className="text-green-600 hover:text-green-800"><Check size={16} /></button>
                      <button onClick={() => { setNewPhraseCategoryId(null); setNewPhraseText(''); }} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setNewPhraseCategoryId(cat.id)}
                      className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 mt-2 py-1"
                    >
                      <Plus size={12} /><span>Add phrase</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default CategoriesTab;
